import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ThumbsUp, ThumbsDown, MessageCircle, Lightbulb, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FeaturedSuggestion() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [suggestion, setSuggestion] = useState(null);
  const [userVote, setUserVote] = useState(null);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    fetchFeatured();
  }, []);

  const fetchFeatured = async () => {
    try {
      const res = await fetch(`${API}/api/beta-feedback/featured/suggestions?limit=1`);
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) setSuggestion(data[0]);
      }
    } catch (err) {
      // Silently fail
    }
  };

  const handleVote = async (direction) => {
    if (!user) {
      toast.info('Sign in to vote on suggestions');
      return;
    }
    setVoting(true);
    try {
      const res = await fetch(`${API}/api/beta-feedback/${suggestion.id}/vote`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ direction })
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestion(prev => ({ ...prev, upvotes: data.upvotes, downvotes: data.downvotes, vote_score: data.vote_score }));
        setUserVote(data.user_vote);
      }
    } catch (err) {
      toast.error('Error voting');
    } finally {
      setVoting(false);
    }
  };

  if (!suggestion) return null;

  return (
    <Card className="border-dashed border-2 border-amber-200 bg-amber-50/50" data-testid="featured-suggestion">
      <CardContent className="py-4 px-5">
        <div className="flex items-start gap-4">
          <div className="flex flex-col items-center gap-1 shrink-0">
            <button
              onClick={() => handleVote('up')}
              disabled={voting}
              className={`p-1.5 rounded-lg transition-colors ${userVote === 'up' ? 'bg-green-100 text-green-600' : 'hover:bg-green-50 text-gray-400'}`}
              data-testid="featured-vote-up"
            >
              <ThumbsUp className="h-5 w-5" />
            </button>
            <span className={`text-lg font-bold ${(suggestion.vote_score || 0) > 0 ? 'text-green-600' : 'text-gray-400'}`}
              data-testid="featured-vote-score">
              {suggestion.vote_score || 0}
            </span>
            <button
              onClick={() => handleVote('down')}
              disabled={voting}
              className={`p-1.5 rounded-lg transition-colors ${userVote === 'down' ? 'bg-red-100 text-red-500' : 'hover:bg-red-50 text-gray-400'}`}
              data-testid="featured-vote-down"
            >
              <ThumbsDown className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              <span className="text-xs font-semibold uppercase tracking-wide text-amber-600">Featured Suggestion</span>
            </div>
            <h3 className="font-semibold text-gray-900" data-testid="featured-suggestion-title">{suggestion.title}</h3>
            {suggestion.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{suggestion.description}</p>
            )}
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs text-gray-400">
                {suggestion.upvotes || 0} votes
              </span>
              {suggestion.reply_count > 0 && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <MessageCircle className="h-3 w-3" /> {suggestion.reply_count}
                </span>
              )}
              <Button
                variant="link"
                size="sm"
                className="text-xs p-0 h-auto text-amber-600"
                onClick={() => navigate('/beta-feedback?tab=suggestions')}
                data-testid="see-all-suggestions"
              >
                See all suggestions <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
